import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, Card, CardHeader, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { getRouteByAccountType } from '../../../routes/getRouteByAccountType';
import { PlayerProfileDomain } from '../playerProfile.domain';
import { di } from '../../../state/inversify.config';
import { getInitials } from '../../../helpers/initialsExtractor';
import { AccountTypes } from '../../../components/changeUser/accountTypes.enum';
import { CreateIncidentDomainService } from '../../createIncident/createIncidentDomainService';
import { Avatar } from '../../../components/avatar/avatar';
import { Button } from '../../../components/button/button';

export function PlayerProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const playerProfileDomain = di.get<PlayerProfileDomain>(PlayerProfileDomain);
  const user = playerProfileDomain.useUserSelector();
  const player = playerProfileDomain.useCurrentPlayerSelector();
  const openPlayerIncident =
    playerProfileDomain.useOpenIncidentByPlayerSelector();
  const createIncidentDomainService = di.get<CreateIncidentDomainService>(
    CreateIncidentDomainService,
  );

  const onCreateNewIncident = () => {
    createIncidentDomainService.dispatchSkipPlayerPageQuery();
    navigate(
      `/${getRouteByAccountType(
        user?.accountType,
      )}/create-incident?selectedPlayerId=${player?.id}`,
    );
  };

  const viewOpenIncident = () => {
    navigate(
      `/${getRouteByAccountType(
        user?.accountType,
      )}/view-incident?selectedPlayerId=${player?.id}&selectedIncidentId=${
        openPlayerIncident.id
      }`,
    );
  };

  const onNavigateToIncidentList = () => {
    navigate(
      `/${getRouteByAccountType(
        user?.accountType,
      )}/incidents-list?selectedPlayerId=${player?.id}`,
    );
  };

  const onNavigateToCreateAssessment = () => {
    navigate(
      `/${getRouteByAccountType(
        user?.accountType,
      )}/assessment?selectedPlayerId=${player?.id}`,
    );
  };

  const incidentAction = () => {
    if (openPlayerIncident)
      return (
        <Button
          color="secondary"
          type="submit"
          variant="contained"
          onClick={viewOpenIncident}
          startIcon={<AddIcon />}
          label={t('VIEW_OPEN_INCIDENT')}
        />
      );

    if (
      user?.accountType === AccountTypes.Guardian ||
      user?.accountType === AccountTypes.Org
    )
      return (
        <Button
          color="secondary"
          testId="createNewIncident"
          type="submit"
          variant="contained"
          onClick={onCreateNewIncident}
          startIcon={<AddIcon />}
          label={t('CREATE_NEW_INCIDENT')}
        />
      );

    return null;
  };

  const playerFirstName: string = player?.firstName || '';

  return (
    <>
      <Box display="flex" alignItems="center" columnGap={2}>
        <Avatar title={getInitials(`${player.firstName} ${player.lastName}`)} />
        <Typography variant="h6">
          {player?.firstName} {player?.lastName}
        </Typography>
        {player?.teamName && (
          <Typography variant="h6">{player.teamName}</Typography>
        )}
        {player?.clearedToPlay ? (
          <CheckCircleIcon sx={{ fill: theme.palette.success.main }} />
        ) : (
          <CancelIcon sx={{ fill: theme.palette.error.main }} />
        )}
      </Box>
      <Box display="flex" alignItems="center" columnGap={2}>
        <Card
          onClick={onNavigateToIncidentList}
          sx={{
            borderRadius: 4,
            mt: 2,
            mb: 2,
            '&:hover': {
              cursor: 'pointer',
            },
          }}
        >
          <CardHeader
            titleTypographyProps={{ variant: 'h6' }}
            title={t('PLAYER_PROFILE_VIEW_INCIDENT_HISTORY', {
              playerFirstName,
            }).toString()}
            action={<ArrowForwardIosOutlinedIcon sx={{ mt: 1 }} />}
            sx={{ columnGap: 2 }}
          />
        </Card>
      </Box>
      <Box display="flex" alignItems="center" columnGap={2}>
        {incidentAction()}
        {user?.accountType === AccountTypes.Clinic && (
          <Button
            color="secondary"
            type="submit"
            variant="contained"
            onClick={onNavigateToCreateAssessment}
            startIcon={<AddIcon />}
            label={t('PLAYER_PROFILE_CREATE_ASSESSMENT')}
          />
        )}
      </Box>
    </>
  );
}
